import type { FlatMappedGtmEventPayload, MappedGtmEventName } from '../core/gtm.types';
import type { PushCustomGtmEventOptions } from '../gtm.utils';
import type { UserDto } from '@/queries/user/user.schema';
import { isStaging } from '@/libs/getEnvironment';
import { MAPPED_GTM_EVENTS } from '../core/constants';
import { gtmUserProfileStore } from '../core/gtmUserProfileStore';
import { buildEventId, hasDataLayerAccess, normalizeCustomGtmEventArgs, pushEvent, resolveAttributionFields, resolveBrowserId, resolveDefaultContent, sanitizeEventPayload } from '../gtm.utils';

export function pushCustomGtmEventToDataLayer(
  eventName: MappedGtmEventName,
  user?: Partial<UserDto>,
): void;
export function pushCustomGtmEventToDataLayer(options: PushCustomGtmEventOptions): void;

/**
 * Pushes a custom GTM event to the dataLayer.
 *
 * @param eventOrOptions - The event name or an options object containing the event name and additional data.
 * @param userArg - The user data, if the first argument is the event name.
 */
export function pushCustomGtmEventToDataLayer(
  eventOrOptions: MappedGtmEventName | PushCustomGtmEventOptions,
  userArg?: Partial<UserDto>,
): void {
  // Normalize the arguments to ensure we have a consistent options object.
  const { eventName, user, auth_source } = normalizeCustomGtmEventArgs(eventOrOptions, userArg);

  if (!MAPPED_GTM_EVENTS.includes(eventName)) {
    if (isStaging()) {
      console.warn(`[GTM] Attempted to push unmapped event "${eventName}" to dataLayer. This event will not be sent. Please use one of the predefined events: ${MAPPED_GTM_EVENTS.join(', ')}`);
    }
    return;
  }

  if (!hasDataLayerAccess()) {
  // If the dataLayer is not accessible, we cannot push the event.
    return;
  }

  // Build the event payload with all necessary fields.
  const eventId = buildEventId(eventName);
  // Resolve default content for the event, if any.
  const defaultContent = resolveDefaultContent(eventName);

  const eventData = sanitizeEventPayload({
    auth_source,
    event: eventName,
    event_id: eventId,
    browser_id: resolveBrowserId(),
    event_time: Math.floor(Date.now() / 1000),
    ...defaultContent,
    ...gtmUserProfileStore.resolveAnalyticsUserFields(user),
    ...resolveAttributionFields(),
  }) as FlatMappedGtmEventPayload;

  // Push the event to the dataLayer.
  pushEvent(eventData);

  // Log the event data in staging for debugging purposes.
  if (isStaging()) {
    // eslint-disable-next-line no-console
    console.log('[GTM] Custom event pushed to dataLayer:', eventData);
  }
}

/**
 * @deprecated Prefer pushCustomGtmEventToDataLayer() and pass flat payload fields.
 */
export function pushToDataLayer(
  eventName: string,
  eventParams: Record<string, unknown> = {},
): void {
  if (!hasDataLayerAccess()) {
    return;
  }

  const eventData = sanitizeEventPayload({
    event: eventName,
    event_id: buildEventId(eventName),
    event_time: Math.floor(Date.now() / 1000),
    ...resolveAttributionFields(),
    browser_id: resolveBrowserId(),
    ...eventParams,
  });

  pushEvent(eventData as FlatMappedGtmEventPayload);

  if (isStaging()) {
    // eslint-disable-next-line no-console
    console.log('[GTM] Event pushed to dataLayer:', eventData);
  }
}
